// SEQ-180 follow-up (14 September 2026): one place that says, in plain words,
// what each "Group by" choice does.
//
// WHY THIS EXISTS. The dashboard builder let anyone group a widget by project,
// month, builder or seven other things, and then never named the grouping
// anywhere the reader could see it. A tooltip showed a number and a metric
// name; a table widget headed its first column "Group"; the editor's control
// offered bare nouns. Someone who had not built the widget could not tell what
// a bar stood for. Josh's team said exactly that.
//
// ONE SOURCE FOR THE COPY. The editor's option labels, the tooltip's grouping
// line, the caption under a widget title and a table widget's first column
// heading all read from this table, so the page cannot describe the same
// grouping two different ways. groupByVocabulary.test.ts pins the copy and
// checks that every grain the schema accepts has an entry, so a new grain
// cannot ship with no explanation.
//
// HOUSE STYLE FOR THIS COPY. Product words, not modelling words. The reader is
// a site manager or the managing director, not the person who wrote the RPC,
// so "one bar per project, totalled over the dates you pick" beats "aggregates
// the metric by the project dimension". The test bans the modelling words
// outright.

import type { PnlChartType, PnlWidgetGroupBy } from "../../lib/pnl/pnlWidgetSchema";

export interface PnlGroupByExplainer {
  /** The Group by control's option text, and a table widget's first column heading. */
  label: string;
  /** What ONE bucket is, lower case, so it reads inside a sentence: "one bar per project". */
  bucketTerm: string;
  /** The line a chart tooltip carries, so the reader knows why these buckets exist. */
  tooltipLine: string;
  /** One plain sentence under the Group by control in the widget editor. */
  hint: string;
}

/**
 * Every grain `PnlWidgetGroupBy` allows, explained.
 *
 * Keyed by the stored config value, never by the label, because the label is
 * copy and the value is data. `project_month` is in the union (it comes from
 * `PnlRollupGrain`) but is not offered in the editor today; it still has an
 * entry, because a config carrying it can arrive from the jsonb column and
 * would otherwise render a widget with no grouping named at all.
 */
export const PNL_GROUP_BY_VOCABULARY: Record<PnlWidgetGroupBy, PnlGroupByExplainer> = {
  company: {
    label: "Company total",
    bucketTerm: "company",
    tooltipLine: "Every project added together",
    hint: "One total for the whole company over the dates you pick. Nothing is split out by project.",
  },
  project: {
    label: "Project",
    bucketTerm: "project",
    tooltipLine: "Grouped by project",
    hint: "One bar per project, totalled over the dates you pick.",
  },
  month: {
    label: "Month",
    bucketTerm: "month",
    tooltipLine: "Grouped by month",
    hint: "One point per calendar month, always in date order so a trend reads left to right.",
  },
  project_month: {
    label: "Project and month",
    bucketTerm: "project and month",
    tooltipLine: "Grouped by project and month",
    hint: "Every project split month by month, so a long job appears once for each month it ran.",
  },
  quarter: {
    label: "Quarter",
    bucketTerm: "quarter",
    tooltipLine: "Grouped by quarter",
    hint: "One bar per calendar quarter, so January to March is a single figure.",
  },
  calendar_year: {
    label: "Calendar year",
    bucketTerm: "calendar year",
    tooltipLine: "Grouped by calendar year",
    hint: "One bar per calendar year, January to December.",
  },
  financial_year: {
    label: "Financial year",
    bucketTerm: "financial year",
    tooltipLine: "Grouped by financial year",
    hint: "One bar per Australian financial year, July to June.",
  },
  builder: {
    label: "Builder",
    bucketTerm: "builder",
    tooltipLine: "Grouped by builder",
    hint: "Every project for the same builder is added together, so one bar covers all of their jobs.",
  },
  status: {
    label: "Project status",
    bucketTerm: "project status",
    tooltipLine: "Grouped by project status",
    hint: "Projects are added together by status: active, upcoming or completed.",
  },
  project_manager: {
    label: "Project manager",
    bucketTerm: "project manager",
    tooltipLine: "Grouped by project manager",
    hint: "Projects are added together by the project manager named on them. Projects with nobody named go to a no-name group, so a blank is visible rather than dropped.",
  },
  site_manager: {
    label: "Site manager",
    bucketTerm: "site manager",
    tooltipLine: "Grouped by site manager",
    hint: "Projects are added together by the site manager named on them. Projects with nobody named go to a no-name group, so a blank is visible rather than dropped.",
  },
};

/**
 * The explainer for a grain, falling back to a named one rather than throwing.
 *
 * A config is read from a jsonb column and can carry a value written by a
 * newer version of the app. A widget that says "Grouped by area" for a grain
 * this build does not know is worse than nothing, so the fallback says only
 * what is certainly true: these are groups, and it names the raw value so the
 * user can recognise it in the editor.
 */
export function groupByExplainer(groupBy: string): PnlGroupByExplainer {
  const known = PNL_GROUP_BY_VOCABULARY[groupBy as PnlWidgetGroupBy];
  if (known) return known;
  return {
    label: groupBy,
    bucketTerm: "group",
    tooltipLine: `Grouped by ${groupBy}`,
    hint: `This widget groups by "${groupBy}", which this version of the page has no description for.`,
  };
}

/**
 * What one mark on a chart is called, so the caption under a widget names the
 * thing the reader is actually looking at. A KPI card has no marks: it is one
 * number, and `groupingCaption` refuses to caption it.
 */
const CHART_MARK: Record<Exclude<PnlChartType, "KPI">, string> = {
  bar: "bar",
  stacked_bar: "bar",
  line: "point",
  scatter: "point",
  table: "row",
  // SEQ-193: never actually read (groupingCaption special-cases month_on_month
  // below, since "one row per X" is not the right sentence when the table's
  // rows are metrics rather than the groupBy's own buckets), but the Record
  // type covers every PnlChartType and this keeps that exhaustive rather than
  // an `as` cast.
  month_on_month: "row",
  // SEQ-199: groupingCaption special-cases grid, like month_on_month.
  grid: "row",
};

/**
 * The quiet line under a widget's title, naming its grouping where the widget
 * is read rather than only where it was built. Someone looking at a shared
 * dashboard never opens the editor, so the editor's hint alone would reach
 * nobody.
 *
 * Returns null for a KPI card, which shows a single number with its own label
 * and would gain nothing from "one number per company".
 */
export function groupingCaption(chartType: PnlChartType, groupBy: string): string | null {
  if (chartType === "KPI") return null;
  // SEQ-193: a month_on_month table's rows are not "one row per company/
  // project" in the sense every other chart type's groupBy names a bucket
  // the RPC returned; "company" mode's rows are the chosen METRICS, and
  // "project" mode's rows are the widget's own hand-picked project
  // shortlist. Both are said plainly rather than through the shared
  // bucketTerm sentence, which would read "one row per company" for a table
  // that has several metric rows, not one.
  if (chartType === "month_on_month") {
    return groupBy === "project"
      ? "One row per chosen project, one column per month."
      : "One row per chosen metric, one column per month.";
  }
  // SEQ-199: a grid is always one row per project with a column per month.
  if (chartType === "grid") return "One row per project, one column per month, then the summary columns.";
  const explainer = groupByExplainer(groupBy);
  if (groupBy === "company") return "Every project added together into one total.";
  const mark = CHART_MARK[chartType as Exclude<PnlChartType, "KPI">] ?? "bar";
  return `One ${mark} per ${explainer.bucketTerm}.`;
}

/**
 * The invitation to drill in, by chart type.
 *
 * Every bucket on this page can be opened: a project bar becomes that
 * project's months, a month becomes the projects inside it. Nothing said so.
 * A first-time reader had no way to know the chart was more than a picture,
 * which is the same discoverability gap as the unnamed tooltip.
 *
 * Named per chart type rather than written once with the word "bucket",
 * because the reader is looking at a bar, a point or a row.
 */
export const DRILL_DOWN_HINT: Record<Exclude<PnlChartType, "KPI" | "line" | "month_on_month" | "grid">, string> = {
  bar: "Select a bar to break it down.",
  stacked_bar: "Select a bar to break it down.",
  scatter: "Select a point to break it down.",
  table: "Select a row to break it down.",
};

/**
 * Whether a chart type draws a mark a click can land on.
 *
 * A line chart is the one that does not: its points are three pixels of dot on
 * a path, and the generic line chart takes no click handler at all. Inviting a
 * click there would be a promise the page does not keep.
 *
 * SEQ-193: a month_on_month table's cells are not `WidgetBucket`s (a bucket
 * is one value; a month_on_month cell is one row's one month among many), so
 * there is no bucket for a click to hand the page's drill-down panel. Adding
 * that would need its own drill-down shape, not the one this feature already
 * has.
 */
export function offersDrillDown(chartType: PnlChartType): chartType is Exclude<PnlChartType, "KPI" | "line" | "month_on_month" | "grid"> {
  return chartType !== "KPI" && chartType !== "line" && chartType !== "month_on_month" && chartType !== "grid";
}

/** A table widget's first column heading. It used to be the literal word "Group". */
export function groupColumnHeading(groupBy: string): string {
  return groupByExplainer(groupBy).label;
}
